import { useEffect, useMemo, useState } from 'react';
import { Loader2, PlayCircle, RefreshCw, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { gdprAdminApi, type AdminGdprRequestDetailDto, type AdminGdprRequestDto } from '../../../lib/api';

/** Status auf Deutsch statt der englischen Fachwörter (T-0221). */
const STATUS_TEXT: Record<string, string> = {
  pending: 'Offen',
  open: 'Offen',
  requested: 'Offen',
  processing: 'In Bearbeitung',
  completed: 'Erledigt',
  failed: 'Fehlgeschlagen',
};

const toLocal = (value: string | null | undefined): string => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleString('de-DE');
};

export default function GdprRequestsPage() {
  const [items, setItems] = useState<AdminGdprRequestDto[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<AdminGdprRequestDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [runningJob, setRunningJob] = useState(false);
  const [savingAutoSettings, setSavingAutoSettings] = useState(false);

  const [status, setStatus] = useState<'all' | 'pending' | 'processing' | 'completed' | 'failed'>('pending');
  const [type, setType] = useState<'all' | 'export' | 'delete'>('all');
  const [wpUserId, setWpUserId] = useState('');
  const [autoEnabled, setAutoEnabled] = useState(true);
  const [autoIntervalHours, setAutoIntervalHours] = useState('24');
  const [autoLastRunAt, setAutoLastRunAt] = useState<string | null>(null);
  const [lastManualRunSummary, setLastManualRunSummary] = useState<{
    at: string;
    pendingFound: number;
    completed: number;
    failed: number;
    requiresManualReview: number;
  } | null>(null);

  const pendingCount = useMemo(() => items.filter((i) => i.status === 'pending').length, [items]);

  const loadList = async () => {
    setLoading(true);
    try {
      const parsedWpUser = Number(wpUserId || 0);
      const data = await gdprAdminApi.list({
        status: status === 'all' ? undefined : status,
        type: type === 'all' ? undefined : type,
        wpUserId: Number.isFinite(parsedWpUser) && parsedWpUser > 0 ? parsedWpUser : undefined,
        limit: 300,
      });
      setItems(data);
    } catch (e: any) {
      toast.error(e?.message || 'DSGVO-Anfragen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const loadAutoSettings = async () => {
    try {
      const cfg = await gdprAdminApi.getAutoSettings();
      setAutoEnabled(!!cfg.enabled);
      setAutoIntervalHours(String(cfg.intervalHours));
      setAutoLastRunAt(cfg.lastRunAt ?? null);
    } catch (e: any) {
      toast.error(e?.message || 'Auto-Einstellungen konnten nicht geladen werden');
    }
  };

  const loadDetail = async (id: number) => {
    setDetailLoading(true);
    setSelectedId(id);
    try {
      const data = await gdprAdminApi.getOne(id);
      setDetail(data);
    } catch (e: any) {
      toast.error(e?.message || 'Detailansicht konnte nicht geladen werden');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    void Promise.all([loadList(), loadAutoSettings()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processOne = async (item: AdminGdprRequestDto) => {
    if (item.requestType === 'delete') {
      const confirmed = window.confirm(
        'Personen- und Zugangsdaten werden anonymisiert, der Login wird gesperrt. Zahlungsbelege, Aufträge und Vertragsdaten bleiben für gesetzliche Aufbewahrung und Rechtsansprüche als eingeschränkt nutzbare Datensätze erhalten. Fortfahren?',
      );
      if (!confirmed) return;
    }
    const id = item.id;
    setProcessingId(id);
    try {
      await gdprAdminApi.processOne(id, item.requestType === 'delete' ? 'anonymize' : undefined);
      toast.success(item.requestType === 'delete' ? `Anfrage #${id}: Zugang gesperrt und Stammdaten anonymisiert` : `Anfrage #${id} wurde verarbeitet`);
      await loadList();
      if (selectedId === id) {
        await loadDetail(id);
      }
    } catch (e: any) {
      toast.error(e?.message || `Anfrage #${id} konnte nicht verarbeitet werden`);
    } finally {
      setProcessingId(null);
    }
  };

  const runJob = async () => {
    setRunningJob(true);
    try {
      const res = await gdprAdminApi.runJob(200);
      const reviewCount = res.result.requiresManualReview || 0;
      toast.success(
        `Job fertig: ${res.result.completed} erfolgreich, ${res.result.failed} fehlgeschlagen, ${reviewCount} manuell zu prüfen`,
      );
      setLastManualRunSummary({
        at: new Date().toISOString(),
        pendingFound: res.result.pendingFound,
        completed: res.result.completed,
        failed: res.result.failed,
        requiresManualReview: reviewCount,
      });
      await loadList();
      await loadAutoSettings();
    } catch (e: any) {
      toast.error(e?.message || 'DSGVO-Job konnte nicht gestartet werden');
    } finally {
      setRunningJob(false);
    }
  };

  const saveAutoSettings = async () => {
    const parsed = Math.max(1, Math.min(168, Number(autoIntervalHours || '24')));
    setSavingAutoSettings(true);
    try {
      const saved = await gdprAdminApi.updateAutoSettings({
        enabled: autoEnabled,
        intervalHours: parsed,
      });
      setAutoEnabled(saved.enabled);
      setAutoIntervalHours(String(saved.intervalHours));
      setAutoLastRunAt(saved.lastRunAt ?? null);
      toast.success('DSGVO Auto-Verarbeitung gespeichert');
    } catch (e: any) {
      toast.error(e?.message || 'Auto-Einstellungen konnten nicht gespeichert werden');
    } finally {
      setSavingAutoSettings(false);
    }
  };

  const renderDetailContent = () => {
    if (detailLoading) {
      return (
        <div className="flex items-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Lade Details...
        </div>
      );
    }

    if (!detail) {
      return <div className="text-sm text-muted-foreground">Wähle links eine Anfrage aus.</div>;
    }

    return (
      <>
        <div className="rounded-md border p-3 text-sm">
          <div>
            <span className="font-medium">Anfrage:</span> #{detail.id}
          </div>
          <div>
            <span className="font-medium">Typ:</span> {detail.requestType}
          </div>
          <div>
            <span className="font-medium">Status:</span> {STATUS_TEXT[detail.status] ?? detail.status}
          </div>
          <div>
            <span className="font-medium">WP User:</span> {detail.wpUserId}
          </div>
          <div>
            <span className="font-medium">Retention:</span> {detail.retentionMode ?? '-'}
          </div>
          <div>
            <span className="font-medium">Angefragt:</span> {toLocal(detail.requestedAt)}
          </div>
          <div>
            <span className="font-medium">Verarbeitet:</span> {toLocal(detail.processedAt)}
          </div>
        </div>

        <div className="max-h-[520px] space-y-2 overflow-y-auto rounded-md border p-3">
          {detail.events.length === 0 ? (
            <div className="text-sm text-muted-foreground">Keine Events vorhanden.</div>
          ) : (
            detail.events.map((event) => (
              <div key={event.id} className="rounded-md border p-2">
                <div className="mb-1 text-xs text-muted-foreground">
                  {event.eventType} · {toLocal(event.createdAt)}
                </div>
                <pre className="whitespace-pre-wrap break-all text-xs">
                  {event.eventData ? JSON.stringify(event.eventData, null, 2) : '{}'}
                </pre>
              </div>
            ))
          )}
        </div>
      </>
    );
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">DSGVO-Anfragen</h1>
        <p className="text-muted-foreground">Admin-Übersicht für Datenauskunft und Löschanfragen inkl. manueller Verarbeitung.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Automatische Verarbeitung</CardTitle>
          <CardDescription>Tägliche/periodische Ausführung beim Eingang neuer DSGVO-Anfragen.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-md border p-3">
            <div>
              <div className="font-medium">Auto-Run aktiv</div>
              <div className="text-sm text-muted-foreground">Wenn aktiv, wird gemäß Intervall automatisch verarbeitet.</div>
            </div>
            <Switch checked={autoEnabled} onCheckedChange={setAutoEnabled} disabled={savingAutoSettings || loading || runningJob} />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Intervall (Stunden)</Label>
              <Input
                type="number"
                min={1}
                max={168}
                value={autoIntervalHours}
                onChange={(e) => setAutoIntervalHours(e.target.value)}
                disabled={savingAutoSettings || loading || runningJob}
              />
            </div>
            <div className="space-y-2">
              <Label>Letzter Auto-Run</Label>
              <Input value={toLocal(autoLastRunAt)} disabled />
            </div>
            <div className="flex items-end">
              <div className="flex gap-2">
                <Button onClick={() => void saveAutoSettings()} disabled={savingAutoSettings || loading || runningJob}>
                  {savingAutoSettings ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Einstellungen speichern
                </Button>
                <Button variant="outline" onClick={() => void runJob()} disabled={runningJob || loading || savingAutoSettings}>
                  {runningJob ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlayCircle className="mr-2 h-4 w-4" />}
                  Jetzt Auto-Run ausführen
                </Button>
              </div>
            </div>
          </div>
          {lastManualRunSummary ? (
            <div className="rounded-md border p-3 text-sm text-muted-foreground">
              Letzter Lauf: {toLocal(lastManualRunSummary.at)} · gefunden: {lastManualRunSummary.pendingFound} · erfolgreich:{' '}
              {lastManualRunSummary.completed} · fehlgeschlagen: {lastManualRunSummary.failed} · manuell zu prüfen:{' '}
              {lastManualRunSummary.requiresManualReview}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Filter & Aktionen
          </CardTitle>
          <CardDescription>
            Offene Anfragen: <span className="font-medium">{pendingCount}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as any)} disabled={loading}>
                <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="pending">{STATUS_TEXT.pending}</SelectItem>
                  <SelectItem value="processing">{STATUS_TEXT.processing}</SelectItem>
                  <SelectItem value="completed">{STATUS_TEXT.completed}</SelectItem>
                  <SelectItem value="failed">{STATUS_TEXT.failed}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Typ</Label>
              <Select value={type} onValueChange={(v) => setType(v as any)} disabled={loading}>
                <SelectTrigger><SelectValue placeholder="Typ" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="export">Datenauskunft</SelectItem>
                  <SelectItem value="delete">Löschung</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Kundennummer</Label>
              <Input value={wpUserId} onChange={(e) => setWpUserId(e.target.value)} placeholder="z. B. 123" disabled={loading} />
            </div>
            <div className="flex items-end gap-2">
              <Button variant="outline" onClick={() => void loadList()} disabled={loading}>
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Laden
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Anfragen ({items.length})</CardTitle>
            <CardDescription>Neueste Anfragen zuerst. Klick auf einen Eintrag zeigt die Event-Historie.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {items.length === 0 ? (
              <div className="text-sm text-muted-foreground">Keine DSGVO-Anfragen gefunden.</div>
            ) : (
              items.map((item) => (
                // Kein Knopf im Knopf: Die Zeile ist ein Container, der Detail-
                // Knopf und „Verarbeiten“ stehen nebeneinander.
                <div
                  key={item.id}
                  className={`w-full rounded-md border p-3 transition-colors hover:bg-accent/30 ${
                    selectedId === item.id ? 'border-primary/50 bg-accent/20' : ''
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => void loadDetail(item.id)}
                      className="min-w-0 flex-1 space-y-1 text-left text-sm"
                    >
                      <div className="font-medium">
                        #{item.id} · {item.requestType === 'export' ? 'Datenauskunft' : 'Löschung'} · {STATUS_TEXT[item.status] ?? item.status}
                      </div>
                      <div className="text-muted-foreground">
                        WP User: {item.wpUserId} · Angefragt: {toLocal(item.requestedAt)} · Verarbeitet: {toLocal(item.processedAt)}
                      </div>
                      {item.retentionMode ? (
                        <div className="text-muted-foreground">Retention: {item.retentionMode}</div>
                      ) : null}
                      {item.errorMessage ? (
                        <div className="text-red-600">Fehler: {item.errorMessage}</div>
                      ) : null}
                    </button>
                    <Button
                      variant="outline"
                      onClick={() => void processOne(item)}
                      disabled={item.status !== 'pending' || processingId === item.id || loading || runningJob}
                    >
                      {processingId === item.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      {item.requestType === 'delete' ? 'Prüfen & anonymisieren' : 'Verarbeiten'}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Detailansicht</CardTitle>
            <CardDescription>Request-Daten inkl. Event-Audit-Trail.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">{renderDetailContent()}</CardContent>
        </Card>
      </div>
    </div>
  );
}

